import { Check, Clock, Flame } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { AppWithStats } from '@/core/types';
import { getAppIcon } from '@/lib/app-icons';

export function AppTile({ app }: { app: AppWithStats }) {
  if (app.status === 'coming_soon') {
    return <ComingSoonTile app={app} />;
  }
  return <ActiveTile app={app} />;
}

function ActiveTile({ app }: { app: AppWithStats }) {
  const navigate = useNavigate();
  const Icon = getAppIcon(app.icon);
  const stats = app.stats!;

  const handleTileClick = () => navigate(`/apps/${app.id}`);
  const handlePractice = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/apps/${app.id}/practice`);
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={handleTileClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleTileClick();
        }
      }}
      className="focus-visible:ring-ring flex cursor-pointer flex-col transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
    >
      <CardContent className="flex flex-1 flex-col gap-4 p-6 pt-6">
        <div className="flex items-center gap-3">
          <Icon className="text-primary size-7" strokeWidth={2} />
          <h2 className="font-display text-xl font-semibold">{app.display_name}</h2>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Flame className="text-streak size-4" />
            {stats.streak}
          </span>
          {stats.practiced_today ? (
            <span className="text-success inline-flex items-center gap-1">
              <Check className="size-4" /> practiced today
            </span>
          ) : (
            <span className="text-muted-foreground inline-flex items-center gap-1">
              <Clock className="size-4" /> not yet today
            </span>
          )}
        </div>

        <dl className="grid grid-cols-3 gap-2 text-sm">
          <Stat label="learning" value={stats.learning} />
          <Stat label="known" value={stats.known} />
          <Stat label="archived" value={stats.archived} />
        </dl>

        <div className="mt-auto pt-2">
          <Button className="w-full" size="lg" onClick={handlePractice}>
            Practice
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ComingSoonTile({ app }: { app: AppWithStats }) {
  const Icon = getAppIcon(app.icon);
  return (
    <Card className="bg-secondary/40 flex flex-col">
      <CardContent className="flex flex-1 flex-col gap-4 p-6 pt-6">
        <div className="flex items-center gap-3">
          <Icon className="text-muted-foreground size-7" strokeWidth={2} />
          <h2 className="font-display text-muted-foreground text-xl font-semibold">
            {app.display_name}
          </h2>
        </div>
        <Badge variant="muted" className="self-start">
          Coming soon
        </Badge>
        {app.description && <p className="text-muted-foreground text-sm">{app.description}</p>}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
